import React from "react"
import { IconUser } from "@tabler/icons-react"
import routes from "@love-irish/routes"
import { Profile } from "@love-irish/types"
import UserPopover from "@love-irish/user-popover"

export interface ProfileLabelProps {
  profile: Profile
  size?: "sm" | "md" | "lg"
  showAvatar?: boolean
  showUsername?: boolean
  className?: string
}

const sizes = {
  sm: {
    avatar: "w-5 h-5",
    icon: 12,
    text: "text-xs",
  },
  md: {
    avatar: "w-6 h-6",
    icon: 14,
    text: "text-sm",
  },
  lg: {
    avatar: "w-8 h-8",
    icon: 16,
    text: "text-base",
  },
}

export const ProfileLabel: React.FC<ProfileLabelProps> = ({
  profile,
  size = "md",
  showAvatar = true,
  showUsername = true,
  className = "",
}) => {
  const avatarUrl = profile.avatar?.imageUrl || profile.profileIcon?.imageUrl
  const profilePath = routes.profilePath(profile.slug)

  const avatarSize = sizes[size].avatar
  const iconSize = sizes[size].icon
  const textSize = sizes[size].text

  const avatarElement = avatarUrl ? (
    <img
      src={avatarUrl}
      alt={profile.username}
      className={`${avatarSize} object-cover`}
    />
  ) : (
    <div className={`${avatarSize} bg-muted flex items-center justify-center`}>
      <IconUser size={iconSize} className="text-muted-foreground" />
    </div>
  )

  // Avatar only - wrap with tooltip
  if (showAvatar && !showUsername) {
    return (
      <UserPopover
        username={profile.username}
        className={`relative group inline-block hover:opacity-80 transition-opacity ${className}`}
      >
        {avatarElement}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-foreground text-background text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
          {profile.username}
        </div>
      </UserPopover>
    )
  }

  return (
    <UserPopover
      username={profile.slug}
      className={`inline-flex items-center gap-1.5 ${textSize} hover:opacity-80 transition-opacity ${className}`}
    >
      {showAvatar && avatarElement}
      {showUsername && <strong>{profile.username}</strong>}
    </UserPopover>
  )
}

export default ProfileLabel
